import { useCallback, useEffect, useMemo, useState } from 'react';

import { EvarReportMeasurementsPage } from './evar-report-measurements';
import { EvarReportPage1, type EvarReportData } from './evar-report-page1';
import { EvarReportViewsPage } from './evar-report-views';
import {
    buildMeasurementSections,
    MAX_MEASUREMENT_ROWS_PER_PAGE,
    paginateSections,
} from './measurement-sections';

import {
    buildSnapshotPages,
    computeSnapshotPageCount,
    DEFAULT_GRID_LAYOUT,
    parseIsoDate,
    ReportPreviewLayout,
    ReportSnapshotViewportsHost,
    useReportSnapshotHandler,
    type GridLayout,
} from '@/components/report';
import { useCaseMetadata } from '@/hooks/use-case-metadata';
import type { CaseMetadata } from '@/visualization';
import { useCarmViews } from '@/workflows/vascular/common/hooks/use-carm-views';
import { useSizingState } from '@/workflows/vascular/common/hooks/use-sizing-state';
import { EVAR_SNAPSHOT_TEMPLATE } from '@/workflows/vascular/evar/domain/evar-template';
import { useStrategyState } from '@/workflows/vascular/evar/hooks/use-strategy-state';
import { EVAR_CATALOG_DESCRIPTORS } from '@/workflows/vascular/evar/strategy';

// ============================================================================
// Helpers
// ============================================================================

/**
 * Max device-selection rows (catalog header rows + part rows) that still leave
 * useful room for the sizing views on page 1. Beyond this, the views move to
 * a dedicated page so neither the table nor the views get squeezed.
 */
const MAX_INLINE_DEVICE_ROWS = 8;

/**
 * Build initial report data from immutable case data.
 * Case-derived fields are pre-filled; report-specific fields start empty.
 */
function buildInitialReportData(caseData: CaseMetadata): EvarReportData {
    const parts = [caseData.firstname, caseData.lastname].filter(Boolean);
    return {
        patientName: parts.length > 0 ? parts.join(' ') : '',
        dateOfBirth: caseData.dob ?? '',
        gender: caseData.gender ?? '',
        studyDate: parseIsoDate(caseData.createdAt, { caseId: caseData.id }),
        studyDescription: caseData.name,
        // Report-specific - not from case data
        patientId: '',
        interventionDate: '',
        interventionLocation: '',
    };
}

// ============================================================================
// Component
// ============================================================================

/**
 * Report preview container - shows A4 pages in a scrollable area.
 * Displayed in the main viewport area when the Report step is active.
 *
 * Pages scale down to fit the available width (max: 794 px).
 * Report data is initialized from case data (via CaseDataContext) and can
 * be edited inline by the user before export (e.g. to anonymize the report).
 *
 * Page layout:
 *   Page 1 - Patient info, device selection, sizing sheet, 3D viewport
 *            (virtual stent / sketch view will join this page once available)
 *   Page 2 - Sizing views on their own page, only when the device-selection
 *            table is too long to share page 1 with them
 *   Next   - Measurements (incl. C-arm views), split across as many pages as
 *            needed at section boundaries (see measurement-sections.ts)
 *   Last   - Snapshot captures (always shown)
 */
export function EvarReportPreview() {
    const caseData = useCaseMetadata();
    const strategy = useStrategyState();
    const sizing = useSizingState();
    // No viewport id: read-only access to the C-arm views, no camera binding.
    const carm = useCarmViews();

    // Device-selection table height: one header row per catalog + one row per part.
    const deviceRowCount = useMemo(() => {
        if (!strategy) return 0;
        const catalogIds = new Set(strategy.assembly.map((entry) => entry.catalogId));
        return catalogIds.size + strategy.assembly.length;
    }, [strategy]);
    const viewsOnOwnPage = deviceRowCount > MAX_INLINE_DEVICE_ROWS;

    // Measurements content split across fixed-height A4 pages at section
    // boundaries; always at least one page (empty fallback).
    const measurementPages = useMemo(() => {
        const sections = buildMeasurementSections(sizing, carm?.views ?? null);
        const pages = paginateSections(sections, MAX_MEASUREMENT_ROWS_PER_PAGE);
        return pages.length > 0 ? pages : [[]];
    }, [sizing, carm]);

    // Signature of the sizing data the template captures depend on. It changes
    // when a captured measurement's value or slice location changes, which
    // triggers a re-capture; it stays null until sizing + case data are ready so
    // we never capture blank/unloaded views.
    const captureSignature = useMemo(() => {
        if (!caseData || !sizing) return null;
        const byId = new Map(
            [
                ...sizing.measurements.lengths,
                ...sizing.measurements.diameters,
                ...sizing.measurements.angles,
                ...sizing.measurements.derived,
            ].map((m) => [m.id, m] as const)
        );
        return EVAR_SNAPSHOT_TEMPLATE.configs
            .map((config) => {
                const m = config.measurementId ? byId.get(config.measurementId) : undefined;
                return m
                    ? `${config.id}:${m.value.toFixed(3)}:${m.navigationNodeId}:${m.hasEditedInputs ? 1 : 0}`
                    : `${config.id}:none`;
            })
            .join('|');
    }, [caseData, sizing]);

    // initialData is null when caseData has not yet been set by the workflow service.
    const initialData = useMemo(() => (caseData ? buildInitialReportData(caseData) : null), [caseData]);
    const [reportData, setReportData] = useState<EvarReportData | null>(initialData);

    // Keep report data in sync if caseData identity changes (e.g., component reused across cases)
    useEffect(() => {
        if (initialData !== null) {
            setReportData(initialData);
        }
    }, [initialData]);

    const handleUpdate = useCallback((patch: Partial<EvarReportData>) => {
        setReportData((prev) => (prev ? { ...prev, ...patch } : prev));
    }, []);

    const { templateSnapshots, userSnapshots, isCapturing, error, registerRef, toggleReportInclusion } =
        useReportSnapshotHandler(EVAR_SNAPSHOT_TEMPLATE, captureSignature);

    const [snapshotLayouts, setSnapshotLayouts] = useState<GridLayout[]>([]);
    const handleChangeSnapshotLayout = useCallback((pageIndex: number, layout: GridLayout) => {
        setSnapshotLayouts((prev) => {
            const next = [...prev];
            while (next.length <= pageIndex) next.push(DEFAULT_GRID_LAYOUT);
            next[pageIndex] = layout;
            return next;
        });
    }, []);

    // Fixed content pages: page 1 + optional views page + measurement pages
    const measurementStartPage = viewsOnOwnPage ? 3 : 2;
    const fixedPages = measurementStartPage - 1 + measurementPages.length;
    const snapshotPageCount = computeSnapshotPageCount(
        templateSnapshots.length,
        userSnapshots.length,
        snapshotLayouts,
        { hasError: !!error, isCapturing }
    );
    const totalPages = fixedPages + snapshotPageCount;
    const snapshotStartPage = fixedPages + 1;

    return (
        <>
            {/*
             * Hidden off-screen viewport containers used for snapshot capture.
             * Must be rendered outside ReportPreviewLayout (which has overflow:hidden)
             * so that fixed positioning works correctly.
             */}
            <ReportSnapshotViewportsHost template={EVAR_SNAPSHOT_TEMPLATE} registerRef={registerRef} />

            <ReportPreviewLayout loading={!caseData || !reportData} loadingMessage="Loading patient data…">
                <EvarReportPage1
                    data={reportData!}
                    onUpdate={handleUpdate}
                    totalPages={totalPages}
                    assembly={strategy?.assembly ?? []}
                    catalogDescriptors={EVAR_CATALOG_DESCRIPTORS}
                    introductionSide={strategy?.introductionSide}
                    showViews={!viewsOnOwnPage}
                />
                {viewsOnOwnPage && <EvarReportViewsPage pageNumber={2} totalPages={totalPages} />}
                {measurementPages.map((sections, index) => (
                    <EvarReportMeasurementsPage
                        key={index}
                        sections={sections}
                        pageNumber={measurementStartPage + index}
                        totalPages={totalPages}
                        continued={index > 0}
                    />
                ))}
                {buildSnapshotPages({
                    startPageNumber: snapshotStartPage,
                    totalPages,
                    templateSnapshots,
                    userSnapshots,
                    isCapturing,
                    error,
                    onToggleReportInclusion: toggleReportInclusion,
                    layouts: snapshotLayouts,
                    onChangeLayout: handleChangeSnapshotLayout,
                })}
            </ReportPreviewLayout>
        </>
    );
}
